export type Glyph = {
  id: string;
  icon: string;
  label: string;
  family: "nature" | "cosmos" | "food" | "motion";
};

export type GuessScore = {
  exact: number;
  present: number;
};

export type Guess = {
  glyphs: Glyph[];
  score: GuessScore;
};

export const glyphBank: Glyph[] = [
  { id: "comet", icon: "☄️", label: "Comet", family: "cosmos" },
  { id: "planet", icon: "🪐", label: "Planet", family: "cosmos" },
  { id: "moon", icon: "🌙", label: "Moon", family: "cosmos" },
  { id: "leaf", icon: "🍃", label: "Leaf", family: "nature" },
  { id: "wave", icon: "🌊", label: "Wave", family: "nature" },
  { id: "fox", icon: "🦊", label: "Fox", family: "nature" },
  { id: "lemon", icon: "🍋", label: "Lemon", family: "food" },
  { id: "pepper", icon: "🌶️", label: "Pepper", family: "food" },
  { id: "honey", icon: "🍯", label: "Honey", family: "food" },
  { id: "spark", icon: "⚡", label: "Spark", family: "motion" },
  { id: "kite", icon: "🪁", label: "Kite", family: "motion" },
  { id: "rocket", icon: "🚀", label: "Rocket", family: "motion" }
];

const puzzleLength = 4;
const maxGuesses = 7;
const epoch = Date.UTC(2026, 0, 1);

export const gameRules = {
  puzzleLength,
  maxGuesses
};

export function getTodayKey(date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

export function getPuzzleNumber(date = new Date()): number {
  const utcDay = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return Math.max(1, Math.floor((utcDay - epoch) / 86_400_000) + 1);
}

function seededRandom(seed: number): () => number {
  let value = seed % 2_147_483_647;
  if (value <= 0) {
    value += 2_147_483_646;
  }

  return () => {
    value = (value * 16_807) % 2_147_483_647;
    return (value - 1) / 2_147_483_646;
  };
}

export function getDailyAnswer(date = new Date()): Glyph[] {
  const random = seededRandom(getPuzzleNumber(date) * 9_973);
  const pool = [...glyphBank];
  const answer: Glyph[] = [];

  while (answer.length < puzzleLength) {
    const index = Math.floor(random() * pool.length);
    answer.push(pool.splice(index, 1)[0]);
  }

  return answer;
}

export function scoreGuess(guess: Glyph[], answer: Glyph[]): GuessScore {
  const exact = guess.filter((glyph, index) => answer[index]?.id === glyph.id).length;
  const answerIds = new Set(answer.map((glyph) => glyph.id));
  const presentTotal = guess.filter((glyph) => answerIds.has(glyph.id)).length;

  return {
    exact,
    present: presentTotal - exact
  };
}

export function isWinningScore(score: GuessScore): boolean {
  return score.exact === puzzleLength;
}

export function makeShareText(guesses: Guess[], date = new Date()): string {
  const solved = guesses.some((guess) => isWinningScore(guess.score));
  const scoreLine = solved ? `${guesses.length}/${maxGuesses}` : `X/${maxGuesses}`;
  const rows = guesses
    .map((guess) => {
      const exactMarks = "🟧".repeat(guess.score.exact);
      const presentMarks = "🟦".repeat(guess.score.present);
      const emptyMarks = "⬛".repeat(puzzleLength - guess.score.exact - guess.score.present);
      return `${exactMarks}${presentMarks}${emptyMarks}`;
    })
    .join("\n");

  return `GlyphGrid #${getPuzzleNumber(date)} ${scoreLine}\n${rows}\nPlay the daily emoji logic puzzle`;
}

export function createStarterGuess(answer: Glyph[]): Glyph[] {
  const familySet = new Set(answer.map((glyph) => glyph.family));
  const balanced = glyphBank.filter((glyph) => familySet.has(glyph.family));
  return balanced.slice(0, puzzleLength);
}
